import { useSelector } from "react-redux";

import type { AccountType } from "shared/types/accounts";
import { igAccountNeedsMediaReconnect } from "utils/account-health";
import { getInvalidIgAccount } from "selectors/hashtagFeedSelectors";

/**
 * The wording the legacy library feeds have always used, kept verbatim and in
 * one place so every surface says the same thing.
 */
export const igReconnectMessage = (login?: string): string =>
  `The selected account${
    login ? ` @${login}` : ""
  } does not have the correct credentials. Please reconnect Sked Insights on the accounts page.`;

/**
 * Whether to show the reconnect notice for a Graph-backed media feed.
 *
 * Two independent signals. The account flag is known before any request, so the
 * notice appears immediately rather than after a round trip. The server's own
 * verdict is the backstop, for a token that looks fine locally but is not.
 */
export function igAccountNeedsReconnect(
  account: AccountType | null | undefined,
  invalidIgAccount: boolean
): boolean {
  return invalidIgAccount || igAccountNeedsMediaReconnect(account);
}

export function useIgAccountNeedsReconnect(
  account: AccountType | null | undefined
): boolean {
  const invalidIgAccount = useSelector(getInvalidIgAccount);
  return igAccountNeedsReconnect(account, !!invalidIgAccount);
}

/**
 * Shown in place of a Graph-backed feed. The account picker beside it stays
 * usable, so switching to a connected account is the recovery. Deliberately not
 * a toast: this state persists until the user reconnects, and a toast both
 * disappears before it can be acted on and covers the dialog behind it.
 */
export const IgReconnectNotice = ({
  account,
}: {
  account?: AccountType | null;
}) => (
  <div
    role="status"
    data-testid="ig-reconnect-notice"
    className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600"
  >
    <i
      className="fa-regular fa-triangle-exclamation mt-0.5"
      aria-hidden="true"
    />
    <span className="flex-1">{igReconnectMessage(account?.login)}</span>
  </div>
);
